import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPermissionDetails, getUserPermission } from 'src/clients';
import { RootState } from 'src/redux/reducers/rootReducer';
import { PERMISSION_ACTION_TYPES } from 'src/redux/types/permissionTypes';
import { reportFunctionError } from 'src/utilities/sentry';

export const usePermissionData = () => {
  const dispatch = useDispatch();
  const { isSessionExpired } = useSelector((state: RootState) => state.auth);

  const setLoading = useCallback(
    (loading: boolean) => {
      dispatch({
        type: 'SET_GENERAL_LOADING',
        payload: loading,
      });
    },
    [dispatch],
  );

  const fetchUserPermission = useCallback(async () => {
    if (!isSessionExpired) {
      const response = await getUserPermission();
      if (response.data.data) {
        dispatch({
          type: PERMISSION_ACTION_TYPES.SET_USER_PERMISSIONS,
          payload: response.data.data?.[0]?.permission_list ?? [],
        });
      }
    }
  }, [dispatch, isSessionExpired]);

  const fetchProductPermissions = useCallback(async () => {
    const response = await getPermissionDetails({
      domain: 'BUSINESS_DASHBOARD',
    });
    if (response.data.data) {
      dispatch({
        type: PERMISSION_ACTION_TYPES.SET_PRODUCT_PERMISSIONS,
        payload: response.data.data,
      });
    }
  }, [dispatch]);

  const fetchPermission = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchUserPermission(), fetchProductPermissions()]);
    } catch (exception) {
      reportFunctionError({
        domain: 'dashboard',
        parentName: 'usePermissionData',
        message: 'Failed to fetch permission',
        name: 'fetchPermission',
        exception,
      });
    }
    setLoading(false);
  }, [fetchProductPermissions, fetchUserPermission, setLoading]);

  return { fetchPermission };
};
